import { useMutation, useQuery } from '@apollo/client/react'
import { BarChart3, Loader, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { GQL_ACKNOWLEDGE_STATS_NOTICE, GQL_MY_STATS_NOTICE } from '~/gql/consent/consent'
import { useBranding } from '~/helpers/branding/branding'
import { useAuth } from '~/helpers/store/auth'
import { useStatsNoticeDialog } from '~/helpers/store/consent'

/**
 * Notice d'information RGPD « suivi statistique » (art. 13).
 *
 * Ce n'est pas un recueil de consentement : le suivi d'assiduité repose sur
 * l'exécution du contrat de formation (art. 6.1.b) et sur l'obligation légale de
 * traçabilité de l'organisme (art. 6.1.c). L'apprenant est donc informé, pas
 * sollicité — il n'y a rien à accorder ni à refuser (#133).
 *
 * La modale est présentée une fois par version de notice, se ferme normalement
 * (Échap, clic extérieur, bouton), et son acquittement est journalisé comme preuve
 * d'information. L'accès à l'application n'en dépend pas : fermer sans acquitter
 * laisse simplement la notice à représenter à la prochaine session.
 *
 * Elle reste consultable à tout moment via `useStatsNoticeDialog`.
 */
function StatsNoticeDialog() {
  const { payload } = useAuth()
  const { appName } = useBranding()
  const { t } = useTranslation()
  const { manualOpen, close: closeManual } = useStatsNoticeDialog()

  const isAuthenticated = Boolean(payload)

  const { data, loading, refetch } = useQuery(GQL_MY_STATS_NOTICE, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  })

  const [acknowledgeStatsNotice, { loading: saving }] = useMutation(
    GQL_ACKNOWLEDGE_STATS_NOTICE,
  )

  const notice = data?.myStatsNotice
  // Présentation automatique tant que la version courante n'a pas été acquittée.
  const unacknowledged = Boolean(notice?.needsAcknowledgement)

  const open = isAuthenticated && (manualOpen || unacknowledged)

  const handleOpenChange = (next: boolean) => {
    if (next) return
    closeManual()
  }

  const acknowledge = async () => {
    await acknowledgeStatsNotice()
    await refetch()
    closeManual()
  }

  if (!isAuthenticated) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary">
            <ShieldCheck className="size-5" />
            <DialogTitle>{t('notice.stats.title')}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {t('notice.stats.intro', { appName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm text-muted-foreground">
          <div className="flex items-start gap-2">
            <BarChart3 className="size-4 mt-0.5 shrink-0 text-primary" />
            <p>{t('notice.stats.purpose')}</p>
          </div>

          <ul className="list-disc pl-5 space-y-1">
            <li>{t('notice.stats.data')}</li>
            <li>{t('notice.stats.legalBasis')}</li>
            <li>{t('notice.stats.rights')}</li>
          </ul>

          <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-foreground">
            {t('notice.stats.noConsentNeeded')}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="default"
            disabled={saving || loading}
            onClick={acknowledge}
          >
            {saving && <Loader className="size-4 animate-spin mr-2" />}
            {t('notice.stats.acknowledge')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default StatsNoticeDialog
